import { computed, effect, inject } from '@angular/core';
import { withCallStatus } from '@ngrx-traits/signals';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withHooks,
  withMethods,
  withProps,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  PhoneVerificationInitiateRequest,
  PhoneVerificationVerifyRequest,
  User,
} from '@seatix/shared-domain-auth';
import { injectLocalStorage } from 'ngxtension/inject-local-storage';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { AuthApiService } from '../auth-api/auth-api.service';
import { mapMePhoneError } from '../auth-api/me-phone-error';

const COUNTDOWN_DURATION_SECONDS = 60;

/**
 * El cooldown lleva el número al que se envió el código, no solo su vencimiento.
 * La cubeta del servidor es **por número** (1/minuto), así que un cooldown
 * corriendo para un número no dice nada sobre otro: sin esta identidad, cambiar
 * de teléfono aterrizaba en la pantalla del código afirmando que se había
 * enviado uno al número nuevo, y escondía el reenvío hasta que expirara el
 * cooldown del viejo.
 */
interface PhoneOwnershipCountdown {
  readonly phone: string;
  readonly endTime: number;
}

/**
 * Prueba de posesión del teléfono para quien ya tiene sesión
 * (`/api/auth/me/phone/*`). Es un flujo distinto del sign-in público de
 * `withPhoneAuth`, no una variante: no lleva Turnstile, no emite sesión y
 * enumera los conflictos de propiedad del número a propósito.
 */
export function withPhoneOwnership() {
  return signalStoreFeature(
    // Depende de la sesión emitida por `withAuthentication`, que debe
    // componerse antes en el store.
    type<{
      methods: {
        setUser: (user: User) => void;
      };
    }>(),
    withCallStatus({
      prop: 'phoneOwnershipInitiate',
      errorType: 'string',
    }),
    withCallStatus({
      prop: 'phoneOwnershipVerify',
      errorType: 'string',
    }),
    withProps(() => ({
      // Llave propia, separada de `whatsapp-otp-countdown`: quien acaba de
      // pedir un código en el login público no debe encontrar el reenvío del
      // perfil bloqueado, ni al revés. Las dos cuentas regresivas describen la
      // misma cubeta del servidor (1/min por número, compartida entre ambos
      // flujos), pero cada una solo sabe de sus propios envíos.
      _phoneOwnershipCountdownStorage:
        injectLocalStorage<PhoneOwnershipCountdown | null>(
          'me-phone-verification-countdown',
          {
            defaultValue: null,
          },
        ),
    })),
    withComputed((store) => {
      // Un valor guardado que no tenga las dos piezas se trata como si no
      // hubiera cooldown: el peor caso es cobrar un 429 del servidor, mucho
      // mejor que esconder el reenvío por una llave corrupta.
      const storedCountdown = computed(() => {
        const stored = store._phoneOwnershipCountdownStorage();

        return typeof stored?.phone === 'string' &&
          typeof stored?.endTime === 'number'
          ? stored
          : null;
      });

      const activeCountdown = computed(() => {
        const countdown = storedCountdown();
        return countdown && Date.now() < countdown.endTime ? countdown : null;
      });

      const isPhoneOwnershipCountdownActive = computed(
        () => !!activeCountdown(),
      );

      const phoneOwnershipCountdownEndTime = computed(
        () => activeCountdown()?.endTime ?? null,
      );

      // El número al que se envió el código que sigue en cooldown. Quien lea
      // esto debe compararlo con el número que tiene en pantalla antes de
      // asumir que la persona ya tiene un código en la mano.
      const phoneOwnershipCountdownPhone = computed(
        () => activeCountdown()?.phone ?? null,
      );

      return {
        isPhoneOwnershipCountdownActive,
        phoneOwnershipCountdownEndTime,
        phoneOwnershipCountdownPhone,
      };
    }),
    withMethods((store) => {
      const authApiService = inject(AuthApiService);

      function startPhoneOwnershipCountdown(phone: string) {
        store._phoneOwnershipCountdownStorage.set({
          phone,
          endTime: Date.now() + COUNTDOWN_DURATION_SECONDS * 1000,
        });
      }

      function clearPhoneOwnershipCountdown() {
        store._phoneOwnershipCountdownStorage.set(null);
      }

      // El diálogo llama a esto en cada apertura: sin el reset, un `loaded` de
      // la sesión anterior lo hace aterrizar en la pantalla del código sin
      // haber pedido uno nuevo. Un countdown vivo no se borra aquí — persiste
      // hasta expirar o hasta un verify exitoso, para respetar el cooldown
      // entre reaperturas.
      function resetPhoneOwnershipState() {
        // Los `computed` de arriba memoizan la lectura de `Date.now()`: un
        // cooldown vencido sigue reportándose activo hasta que la señal de
        // storage cambie. Descartarlo aquí, con una escritura, los invalida.
        const stored = store._phoneOwnershipCountdownStorage();

        if (stored && !(Date.now() < stored.endTime)) {
          store._phoneOwnershipCountdownStorage.set(null);
        }

        patchState(store, {
          phoneOwnershipInitiateCallStatus: 'init',
          phoneOwnershipVerifyCallStatus: 'init',
        });
      }

      return {
        startPhoneOwnershipCountdown,
        clearPhoneOwnershipCountdown,
        resetPhoneOwnershipState,

        initiatePhoneOwnership: rxMethod<PhoneVerificationInitiateRequest>(
          pipe(
            switchMap((request) => {
              // Un reenvío invalida el código anterior, así que el error de
              // verify previo deja de aplicar y no debe seguir en pantalla.
              patchState(store, { phoneOwnershipVerifyCallStatus: 'init' });
              store.setPhoneOwnershipInitiateLoading();

              return authApiService.initiatePhoneVerification(request).pipe(
                tap(() => {
                  store.setPhoneOwnershipInitiateLoaded();
                  startPhoneOwnershipCountdown(request.phone);
                }),
                catchError((error) => {
                  store.setPhoneOwnershipInitiateError(mapMePhoneError(error));
                  return EMPTY;
                }),
              );
            }),
          ),
        ),

        verifyPhoneOwnership: rxMethod<PhoneVerificationVerifyRequest>(
          pipe(
            switchMap((request) => {
              store.setPhoneOwnershipVerifyLoading();

              return authApiService.verifyPhoneOwnership(request).pipe(
                tap((response) => {
                  clearPhoneOwnershipCountdown();
                  // `verify` devuelve el perfil completo y es la única vía por
                  // la que `phone_verified_at` cambia en esta sesión:
                  // `userResource` no se re-dispara con mutaciones.
                  store.setUser(response.data);
                  store.setPhoneOwnershipVerifyLoaded();
                }),
                catchError((error) => {
                  store.setPhoneOwnershipVerifyError(mapMePhoneError(error));
                  return EMPTY;
                }),
              );
            }),
          ),
        ),
      };
    }),
    withHooks((store) => ({
      onInit: () => {
        effect(() => {
          const stored = store._phoneOwnershipCountdownStorage();
          const isActive = store.isPhoneOwnershipCountdownActive();

          if (stored && !isActive) {
            store._phoneOwnershipCountdownStorage.set(null);
          }
        });
      },
    })),
  );
}
